/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Avatar,
  // Pagination,
  IconButton,
  Skeleton,
  Icon,
} from "@mui/material";
import SoftPagination from "components/SoftPagination";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { attendenceTeam } from "api/attendance";
import { useDropdownOptions } from "layouts/interviewSelected/hooks/useDropdownOptions";
import UserFilters from "layouts/interviewSelected/components/useFilters";
import FlagLoader from "layouts/flagloader";

const AttendenceTeam = ({ onUserSelect }) => {
  const [teamList, setTeamList] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeView, setActiveView] = useState("All");
  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    day_type: "",
    status: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Use the new lazy-loaded dropdown hook
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchSubBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  // Helper function to determine background color based on day_type
  const getDayTypeColor = (day_type) => {
    switch (day_type) {
      case 'holiday':
        return '#f5f5f5';
      case 'weekend':
        return '#f5f5f5';
      case 'leave':
        return '#f5f5f5';
      case 'working_day':
        return '#f5f5f5';
      default:
        return '#f5f5f5';
    }
  };

  const getMonthlyCardStyle = (user) => {
    switch (user?.monthly_attendance?.card_color) {
      case "green":
        return {
          background: "#ecfdf5",
          border: "1.5px solid #22c55e",
          boxShadow: "0 2px 10px rgba(34, 197, 94, 0.12)",
          textColor: "#15803d",
          badgeBg: "#dcfce7",
        };
      case "yellow":
        return {
          background: "#fffbeb",
          border: "1.5px solid #f59e0b",
          boxShadow: "0 2px 10px rgba(245, 158, 11, 0.12)",
          textColor: "#b45309",
          badgeBg: "#fef3c7",
        };
      case "red":
        return {
          background: "#fef2f2",
          border: "1.5px solid #ef4444",
          boxShadow: "0 2px 10px rgba(239, 68, 68, 0.12)",
          textColor: "#b91c1c",
          badgeBg: "#fee2e2",
        };
      default:
        return {
          background: getDayTypeColor(user?.day_type),
          border: "1px solid #e7e7e7",
          boxShadow: "none",
          textColor: "#64748b",
          badgeBg: "#f8fafc",
        };
    }
  };

  const renderMonthlyAttendanceSummary = (user) => {
    const monthly = user?.monthly_attendance;
    if (!monthly) return null;

    const style = getMonthlyCardStyle(user);

    return (
      <SoftBox
        sx={{
          mt: 0.7,
          mb: 0.5,
          px: 1,
          py: 0.45,
          borderRadius: 1.5,
          background: style.badgeBg,
        }}
      >
        <SoftTypography
          variant="caption"
          fontWeight="medium"
          sx={{
            color: style.textColor,
            display: "block",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {monthly.card_label || "Month in progress"}
        </SoftTypography>
        <SoftTypography
          variant="caption"
          sx={{
            color: style.textColor,
            display: "block",
            fontSize: "0.68rem",
            lineHeight: 1.2,
          }}
        >
          Full {monthly.covered_days || 0}/{monthly.reviewed_days || 0} | &gt;5m{" "}
          {monthly.late_after_5_days || 0} | &gt;10m {monthly.late_after_10_days || 0}
        </SoftTypography>
      </SoftBox>
    );
  };

  // Helper function to get status text and color
  const getStatusInfo = (user) => {
    const { day_type, status, message } = user;

    if (day_type === 'holiday' || day_type === 'weekend') {
      if (status === 'overtime_running') {
        return {
          text: 'Overtime Running',
          color: '#ff9800',
          bgColor: '#fff3e0',
          icon: 'timer'
        };
      }
      if (status === 'overtime_completed') {
        return {
          text: `Overtime: ${user.overtime_minutes} mins`,
          color: '#4caf50',
          bgColor: '#e8f5e9',
          icon: 'check_circle'
        };
      }
      return {
        text: message,
        color: day_type === 'holiday' ? '#388e3c' : '#1976d2',
        bgColor: day_type === 'holiday' ? '#e8f5e9' : '#e3f2fd',
        icon: day_type === 'holiday' ? 'celebration' : 'weekend'
      };
    }

    switch (status) {
      case 'full':
        return {
          text: 'Attendance Completed',
          color: '#4caf50',
          bgColor: '#e8f5e9',
          icon: 'check_circle'
        };
      case 'running':
        return {
          text: 'Attendance Running',
          color: '#ff9800',
          bgColor: '#fff3e0',
          icon: 'timer'
        };
      case 'shift_not_started':
        return {
          text: 'Shift Not Started',
          color: '#757575',
          bgColor: '#f5f5f5',
          icon: 'schedule'
        };
      case 'first_half_leave':
        return {
          text: 'First Half Leave',
          color: '#ff9800',
          bgColor: '#fff3e0',
          icon: 'event_available'
        };
      case 'second_half_leave':
        return {
          text: 'Second Half Leave',
          color: '#ff9800',
          bgColor: '#fff3e0',
          icon: 'event_available'
        };
      case 'no_reason_declared':
        return {
          text: 'Absent',
          color: '#f44336',
          bgColor: '#ffebee',
          icon: 'cancel'
        };
      case 'leave':
        return {
          text: 'On Leave',
          color: '#ff9800',
          bgColor: '#fff3e0',
          icon: 'beach_access'
        };
      default:
        return {
          text: message || 'Unknown',
          color: '#757575',
          bgColor: '#f5f5f5',
          icon: 'help'
        };
    }
  };

  const renderStatusBadge = (user) => {
    const statusInfo = getStatusInfo(user);
    return (
      <SoftBox
        sx={{
          background: statusInfo.bgColor,
          p: 0.1,
          borderRadius: 2,
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ color: statusInfo.color, mr: 0.5, fontSize: '1rem' }}>
          {statusInfo.icon}
        </Icon>
        <SoftTypography
          variant="button"
          fontWeight="medium"
          sx={{ color: statusInfo.color, fontSize: '0.75rem' }}
        >
          {statusInfo.text}
        </SoftTypography>
      </SoftBox>
    );
  };

  const renderAttendanceDetails = (user) => {
    return (
      <SoftBox sx={{ mt: 1 }} display="flex" justifyContent="space-between">
        {user.total_attendance_minutes && (
          <SoftTypography variant="caption" display="block">
            Worked: {Math.floor(user.total_attendance_minutes / 60)}h {user.total_attendance_minutes % 60}m
          </SoftTypography>
        )}
        {user.total_break_minutes && (
          <SoftTypography variant="caption" display="block">
            Break: {user.total_break_minutes}m
          </SoftTypography>
        )}
      </SoftBox>
    );
  };

  // Filter users based on active view tab
  const filterUsersByView = (users) => {
    switch (activeView) {
      case 'Working Days':
        return users.filter(user => user.day_type === 'working_day');
      case 'Leaves':
        return users.filter(user => user.day_type === 'leave' || user.status === 'first_half_leave' || user.status === 'second_half_leave');
      case 'Holidays/Weekends':
        return users.filter(user => user.day_type === 'holiday' || user.day_type === 'weekend');
      default:
        return users;
    }
  };

  // Fetch team list
  const fetchTeamList = useCallback(
    async (currentPage = 1, isInitial = false) => {
      try {
        isInitial ? setPageLoading(true) : setListLoading(true);

        const params = {
          page: currentPage,
          ...filters,
          q: searchTerm,
        };

        Object.keys(params).forEach((k) => {
          if (!params[k] || params[k] === '') delete params[k];
        });

        const response = await attendenceTeam(params);
        const data = response?.data?.data || [];

        setTeamList(data);
        setTotalPages(response?.data?.last_page || 1);
      } catch (err) {
        console.error("Error loading team data:", err);
        setTeamList([]);
      } finally {
        setPageLoading(false);
        setListLoading(false);
      }
    },
    [filters, searchTerm]
  );

  // Initial load only team API
  useEffect(() => {
    fetchTeamList(page, true);
  }, []); // Empty deps ensures only once on mount

  // When page, filters, or searchTerm change, fetch team list (with debounce maybe, but immediate as per requirement)
  useEffect(() => {
    if (!pageLoading) {
      fetchTeamList(page, false);
    }
  }, [page, filters, searchTerm]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const SkeletonCard = (
    <Card sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <Skeleton variant="circular" width={50} height={50} />
      <Skeleton sx={{ mt: 2 }} height={20} width="60%" />
      <Skeleton height={15} width="40%" />
    </Card>
  );

  if (pageLoading) {
    return (
      // <SoftBox>
      //   <Row className="g-4">
      //     {Array.from({ length: 8 }).map((_, i) => (
      //       <Col key={i} xs={12} sm={6} md={3}>
      //         {SkeletonCard}
      //       </Col>
      //     ))}
      //   </Row>
      // </SoftBox>
      <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <FlagLoader />    {/* YAHAN ARRAY MAP HATA KAR FLAG LOADER RAKH DIYA */}
      </SoftBox>
    );
  }

  const getPaginationRange = () => {
    const total = totalPages;
    const current = page;
    const limit = 5;

    let start = Math.max(1, current - Math.floor(limit / 2));
    let end = start + limit - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - limit + 1);
    }

    const pages = [];

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total) {
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  const filteredUsers = filterUsersByView(teamList);

  return (
    <SoftBox sx={{ position: "relative", pb: "80px" }}>
      {!selectedUser ? (
        <Card>
          <SoftBox px={3} py={1}>
            <UserFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              // Pass down the options and loading states
              dropdownOptions={dropdownOptions}
              loading={dropdownLoading}
              // Pass down fetch functions
              fetchBranches={fetchBranches}
              fetchSubBranches={fetchSubBranches}
              fetchShifts={fetchShifts}
              fetchDepartments={fetchDepartments}
              fetchDesignations={fetchDesignations}
              fetchRoles={fetchRoles}
              pageContext="Attendance.Team"
            />

            <SoftBox my={1}>
              {filteredUsers.length === 0 ? (
                <SoftBox textAlign="center" py={5}>
                  <Icon sx={{ fontSize: 60, color: '#ccc', mb: 2 }}>
                    sentiment_dissatisfied
                  </Icon>
                  <SoftTypography variant="h6" color="textSecondary">
                    No users found for {activeView} view
                  </SoftTypography>
                </SoftBox>
              ) : (
                <Row className="g-2">
                  {listLoading
                    // ? Array.from({ length: 8 }).map((_, i) => (
                    //   <Col key={i} xs={12} sm={6} md={3}>
                    //     {SkeletonCard}
                    //   </Col>
                    // ))
                    ? (
                      <SoftBox display="flex" justifyContent="center" width="100%" py={4}>
                        <FlagLoader />    {/* YAHAN LIST LOADING PAR FLAG LOADER LAGAYA */}
                      </SoftBox>
                    )
                    : filteredUsers.map((user) => (
                      <Col key={user.id} xs={12} sm={6} md={3}>
                        <Card
                          onClick={() => onUserSelect(user)}
                          sx={{
                            px: 1,
                            pt: 1,
                            borderRadius: 3,
                            height: "100%",
                            cursor: "pointer",
                            border: getMonthlyCardStyle(user).border,
                            background: getMonthlyCardStyle(user).background,
                            boxShadow: getMonthlyCardStyle(user).boxShadow,
                            transition: "all 0.35s ease",
                            "&:hover": {
                              transform: "translateY(-6px) scale(1.02)",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                            },
                          }}
                        >
                          <SoftBox display="flex" alignItems="center">
                            <Avatar
                              src={user.photo || ""}
                              sx={{
                                width: 35,
                                height: 35,
                                bgcolor: "#3f51b5",
                                color: "white",
                                mr: 1.5,
                                fontSize: '1rem'
                              }}
                            >
                              {!user.photo &&
                                user.name
                                  ?.split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .slice(0, 2)
                                  .toUpperCase()}
                            </Avatar>
                            <SoftBox sx={{ overflow: 'hidden' }}>
                              <SoftTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                  fontSize: '0.9rem',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {user.name} {user.last_name}
                              </SoftTypography>
                              <SoftTypography
                                variant="caption"
                                color="secondary"
                                sx={{ fontSize: '0.75rem' }}
                              >
                                {user.shift ? `${user.shift.start_time} - ${user.shift.end_time}` : 'No Shift'}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                          {renderMonthlyAttendanceSummary(user)}
                          {renderStatusBadge(user)}
                          {renderAttendanceDetails(user)}
                        </Card>
                      </Col>
                    ))}
                </Row>
              )}
            </SoftBox>
          </SoftBox>

          {filteredUsers.length > 0 && (
            <SoftBox
              sx={{
                position: "fixed",
                width: "90%",
                bottom: 0,
                background: "#fff",
                pt: 1,
                zIndex: 20,
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {/* <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="info"
                variant="gradient"
              /> */}

              <SoftPagination variant="gradient" color="info">

                {/* FIRST */}
                <SoftPagination
                  item
                  disabled={page === 1}
                  onClick={() => {
                    setPage(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  «
                </SoftPagination>

                {/* PREV */}
                <SoftPagination
                  item
                  disabled={page === 1}
                  onClick={() => {
                    setPage((p) => p - 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  ‹
                </SoftPagination>

                {/* PAGE NUMBERS */}
                {getPaginationRange().map((item, i) => {
                  if (item === "...") {
                    return (
                      <SoftPagination key={i} item disabled>
                        ...
                      </SoftPagination>
                    );
                  }

                  return (
                    <SoftPagination
                      key={i}
                      item
                      active={page === item}
                      onClick={() => {
                        setPage(item);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      {item}
                    </SoftPagination>
                  );
                })}

                {/* NEXT */}
                <SoftPagination
                  item
                  disabled={page === totalPages}
                  onClick={() => {
                    setPage((p) => p + 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  ›
                </SoftPagination>

                {/* LAST */}
                <SoftPagination
                  item
                  disabled={page === totalPages}
                  onClick={() => {
                    setPage(totalPages);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  »
                </SoftPagination>

              </SoftPagination>
            </SoftBox>
          )}
        </Card>
      ) : (
        <>
          <SoftBox display="flex" alignItems="center" mb={1}>
            <IconButton onClick={() => setSelectedUser(null)}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <SoftTypography variant="h6" fontWeight="bold">
              {selectedUser.name} {selectedUser.last_name}&apos;s Attendance Details
            </SoftTypography>
          </SoftBox>

          <Card sx={{ p: 3, borderRadius: 3 }}>
            <SoftBox display="flex" alignItems="center" mb={3}>
              <Avatar
                src={selectedUser.photo || ""}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "#3f51b5",
                  color: "white",
                  mr: 2,
                }}
              >
                {!selectedUser.photo &&
                  selectedUser.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
              </Avatar>
              <SoftBox>
                <SoftTypography variant="h5" fontWeight="bold">
                  {selectedUser.name} {selectedUser.last_name}
                </SoftTypography>
                <SoftTypography variant="body2" color="textSecondary">
                  Day Type: <strong>{selectedUser.day_type?.replace('_', ' ').toUpperCase()}</strong>
                </SoftTypography>
                {selectedUser.shift && (
                  <SoftTypography variant="body2" color="textSecondary">
                    Shift: {selectedUser.shift.start_time} - {selectedUser.shift.end_time}
                  </SoftTypography>
                )}
              </SoftBox>
            </SoftBox>

            <SoftBox
              sx={{
                p: 2,
                borderRadius: 2,
                background: getDayTypeColor(selectedUser.day_type),
                mb: 2,
              }}
            >
              <SoftTypography variant="h6" fontWeight="bold" gutterBottom>
                Current Status
              </SoftTypography>
              <SoftBox display="flex" alignItems="center">
                {renderStatusBadge(selectedUser)}
              </SoftBox>
              {selectedUser.message && (
                <SoftTypography variant="body2" sx={{ mt: 1 }}>
                  {selectedUser.message}
                </SoftTypography>
              )}
            </SoftBox>

            <SoftBox>
              <SoftTypography variant="h6" fontWeight="bold" gutterBottom>
                Attendance Details
              </SoftTypography>
              <Row>
                {selectedUser.scheduled_start && (
                  <Col xs={12} sm={6}>
                    <SoftTypography variant="body2">
                      <strong>Scheduled Start:</strong> {new Date(selectedUser.scheduled_start).toLocaleString()}
                    </SoftTypography>
                  </Col>
                )}
                {selectedUser.scheduled_end && (
                  <Col xs={12} sm={6}>
                    <SoftTypography variant="body2">
                      <strong>Scheduled End:</strong> {new Date(selectedUser.scheduled_end).toLocaleString()}
                    </SoftTypography>
                  </Col>
                )}
                {selectedUser.total_attendance_minutes && (
                  <Col xs={12} sm={6}>
                    <SoftTypography variant="body2">
                      <strong>Total Attendance:</strong> {selectedUser.total_attendance_minutes} minutes
                    </SoftTypography>
                  </Col>
                )}
                {selectedUser.total_break_minutes && (
                  <Col xs={12} sm={6}>
                    <SoftTypography variant="body2">
                      <strong>Total Break:</strong> {selectedUser.total_break_minutes} minutes
                    </SoftTypography>
                  </Col>
                )}
                {selectedUser.overtime_start && (
                  <Col xs={12} sm={6}>
                    <SoftTypography variant="body2">
                      <strong>Overtime Start:</strong> {new Date(selectedUser.overtime_start).toLocaleString()}
                    </SoftTypography>
                  </Col>
                )}
                {selectedUser.overtime_minutes && (
                  <Col xs={12} sm={6}>
                    <SoftTypography variant="body2">
                      <strong>Overtime Duration:</strong> {selectedUser.overtime_minutes} minutes
                    </SoftTypography>
                  </Col>
                )}
              </Row>
            </SoftBox>
          </Card>
        </>
      )}
    </SoftBox>
  );
};

export default AttendenceTeam;
